import { useEffect, useState, useMemo, useRef, type CSSProperties } from "react";
import { Device } from "@twilio/voice-sdk";
import { supabase } from "../../lib/supabase";
import { callFn, generateGoogleReviewDraft } from "../../lib/api";
import { whoami } from "../../lib/auth";
import type { SessionUser } from "../../types/auth";
import IncomingCallBanner, { type IncomingAlert } from "../../components/common/IncomingCallBanner";
import { playSpeakeasyChime } from "../../lib/audio";
import { useIsMobile } from "../../lib/useIsMobile";
import { ilikeContainsValue, quoteFilterValue } from "../../lib/postgrestFilter";

interface ThreadItem {
  id: string;
  kind: "conversation" | "review";
  status: string;
  priority: string;
  customer_id: string | null;
  customer_name: string;
  identity_norm: string;
  intent: string | null;
  summary: string | null;
  disposition: string | null;
  last_inbound_at: string | null;
  last_inbound_channel: string | null;
  parked_at: string | null;
  resolved_at: string | null;
  last_outbound_at: string | null;
  last_outbound_by: string | null;
  last_outbound_by_name: string | null;
  last_message: string | null;
  last_message_at: string | null;
  last_message_channel: string | null;
  last_message_direction: string | null;
  unread_count: string;
  message_count: string;
  effective_score: number;
  stale_24h: boolean;
  created_at: string;
  updated_at: string;
  spam_suspected: boolean;
  is_spam: boolean;
  spam_score: number;
}

interface MessageItem {
  id: string;
  direction: "inbound" | "outbound" | "internal";
  channel: string;
  body: string;
  subject: string | null;
  phone: string | null;
  email_address: string | null;
  media_url: string | null;
  transcription: string | null;
  duration_seconds: number | null;
  status: string;
  is_read: boolean;
  sent_by: string | null;
  sent_by_name: string | null;
  metadata: any;
  created_at: string;
}

interface CustomerProfile {
  id: string;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  sms_consent: boolean;
  email_consent: boolean;
  created_at: string;
  is_member?: boolean;
  tier?: string;
}

import { QUICK_TEMPLATES, AI_DRAFT_TEMPLATES } from "../../config/hospitalityTemplates";

// Staff-typed search text goes into a PostgREST .or() string; quote it so a
// comma or paren cannot add filter clauses. null when nothing is left to match.
function customerSearchFilter(query: string): string | null {
  if (!query.replace(/\*/g, "").trim()) return null;
  const v = ilikeContainsValue(query.trim());
  return `first_name.ilike.${v},last_name.ilike.${v},display_name.ilike.${v},phone.ilike.${v},email.ilike.${v}`;
}

// A new thread's target is a normalized phone or email, and it becomes both a
// filter value and comms_threads.identity_norm. Every customers.phone/email in
// prod normalizes to one of these (2,176 phones, 1,526 emails, 2026-10-17).
const NORM_PHONE = /^\+?\d{7,15}$/;
const NORM_EMAIL = /^[^\s@",()\\]+@[^\s@",()\\]+\.[^\s@",()\\]+$/;

const RESOLUTION_TAGS = [
  "Answered Question",
  "Reservation Handled",
  "Lost & Found Resolved",
  "Private Event Inquired",
  "Concierge / Member Care",
  "General Feedback Noted",
  "No Response Needed",
];

export default function AdminCommunications() {
  const isMobile = useIsMobile(768);

  // State
  const [threads, setThreads] = useState<ThreadItem[]>([]);
  const [selectedThread, setSelectedThread] = useState<ThreadItem | null>(null);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);

  // Mobile View State: "list" | "thread" | "dossier"
  const [mobileView, setMobileView] = useState<"list" | "thread" | "dossier">("list");
  const [mobileDossierOpen, setMobileDossierOpen] = useState(false);

  const [activeFilter, setActiveTab] = useState<"open" | "urgent" | "waiting" | "spam" | "resolved" | "all">("open");
  const [searchQuery, setSearchQuery] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Composer state
  const [replyBody, setReplyBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [composerChannel, setComposerChannel] = useState<"sms" | "email" | "review" | "internal_note">("sms");
  const [sendingReply, setSendingReply] = useState(false);
  const [draftingReply, setDraftingReply] = useState(false);

  // Disposition / Closure state
  const [dispositionStatus, setDispositionStatus] = useState<"resolved" | "waiting_guest" | "waiting_staff" | "escalated" | "spam">("resolved");
  const [dispositionNote, setDispositionNote] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [quickResolveOpen, setQuickResolveOpen] = useState(false);

  // New Message Outbound Modal State
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [newRecipientQuery, setNewRecipientQuery] = useState("");
  const [selectedNewCustomer, setSelectedNewCustomer] = useState<CustomerProfile | null>(null);
  const [newDirectPhone, setNewDirectPhone] = useState("");
  const [newDirectEmail, setNewDirectEmail] = useState("");
  const [newDirectName, setNewDirectName] = useState("");
  const [newChannel, setNewChannel] = useState<"sms" | "email">("sms");
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState<CustomerProfile[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [sendingNewMessage, setSendingNewMessage] = useState(false);

  // Delete Confirm Modal State
  const [deleteTarget, setDeleteTarget] = useState<{ type: "message" | "thread"; id: string; name?: string } | null>(null);
  const [deletingTarget, setDeletingTarget] = useState(false);

  // Link Contact State
  const [isLinkingContact, setIsLinkingContact] = useState(false);
  const [linkContactQuery, setLinkContactQuery] = useState("");
  const [linkContactResults, setLinkContactResults] = useState<CustomerProfile[]>([]);
  const [searchingLinkContact, setSearchingLinkContact] = useState(false);

  // Twilio Voice Softphone State
  const [voiceDevice, setVoiceDevice] = useState<any>(null);
  const [activeCall, setActiveCall] = useState<any>(null);
  const [callState, setCallState] = useState<"idle" | "ringing" | "connected">("idle");
  const [callDuration, setCallDuration] = useState(0);
  const [callTimer, setCallTimer] = useState<any>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [incomingAlert, setIncomingAlert] = useState<IncomingAlert | null>(null);
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  useEffect(() => {
    whoami()
      .then((u) => setCurrentUser(u))
      .catch(() => {});
  }, []);

  // Fetch threads queue
  async function loadQueue() {
    try {
      const { data, error: err } = await supabase.rpc("get_comms_queue", {
        p_filter: activeFilter,
        p_limit: 100,
      });
      if (err) throw err;
      let fetchedThreads = (data as ThreadItem[]) || [];
      
      const reviewThreadsMissingName = fetchedThreads.filter(t => {
        const isRev = t.kind === "review" || t.last_message_channel === "google_review" || t.last_message_channel === "review" || t.identity_norm?.includes("accounts/");
        if (!isRev) return false;
        let name = t.customer_name?.trim() || "";
        if (!name || name.startsWith("review:") || name.includes("accounts/") || name.includes("locations/")) {
           name = t.identity_norm?.trim() || "";
        }
        return (!name || name.startsWith("review:") || name.includes("accounts/") || name.includes("locations/"));
      });

      if (reviewThreadsMissingName.length > 0) {
        const ids = reviewThreadsMissingName.map(t => t.id);
        const { data: firstMsgs } = await supabase
          .from("sms_log")
          .select("comms_thread_id, metadata, sent_by_name")
          .in("comms_thread_id", ids)
          .order("created_at", { ascending: true });

        if (firstMsgs && firstMsgs.length > 0) {
          fetchedThreads = fetchedThreads.map(t => {
            const msg = firstMsgs.find(m => m.comms_thread_id === t.id);
            if (msg) {
              const nameFromMsg = msg.sent_by_name || msg.metadata?.reviewer_name || msg.metadata?.author_name || msg.metadata?.reviewer?.displayName || msg.metadata?.review?.reviewer?.displayName;
              if (nameFromMsg) {
                return { ...t, customer_name: nameFromMsg };
              }
            }
            return t;
          });
        }
      }

      setThreads(fetchedThreads);
    } catch (err: any) {
      console.error("Failed to load communications queue:", err);
      setError("Could not load threads queue.");
    } finally {
      setLoading(false);
    }
  }

  // Load selected thread message history & customer dossier
  async function loadThreadDetail(thread: ThreadItem) {
    setSelectedThread(thread);
    if (isMobile) {
      setMobileView("thread");
    }
    setLoadingThread(true);
    setReplyBody("");
    setEmailSubject("");
    setDispositionNote("");
    setCustomer(null);
    setMessages([]);

    // Default reply channel based on thread kind/channels
    if (thread.kind === "review") {
      setComposerChannel("review");
    } else {
      setComposerChannel(thread.last_inbound_channel === "email" ? "email" : "sms");
    }

    try {
      // 1. Fetch Messages
      const { data: msgs, error: msgsErr } = await supabase.rpc("get_comms_thread", {
        p_thread: thread.id,
      });
      if (msgsErr) throw msgsErr;
      const rawList = (msgs as MessageItem[]) || [];
      const cleanList = rawList.filter(
        (m) => m.status !== "deleted" && m.metadata?.is_deleted !== true && m.body !== "[Message deleted]"
      );
      setMessages(cleanList);

      // 2. Fetch Customer Details (if exists)
      if (thread.customer_id) {
        const { data: cust, error: custErr } = await supabase
          .from("customers")
          .select("*")
          .eq("id", thread.customer_id)
          .maybeSingle();
        if (!custErr && cust) {
          setCustomer(cust as CustomerProfile);
        }
      }

      // 3. Mark thread messages as read
      const { error: readErr } = await supabase
        .from("sms_log")
        .update({ is_read: true })
        .eq("comms_thread_id", thread.id)
        .eq("is_read", false);
      if (!readErr) {
        setThreads((prev) =>
          prev.map((t) => (t.id === thread.id ? { ...t, unread_count: "0" } : t))
        );
      }
    } catch (err: any) {
      console.error("Failed to load thread detail:", err);
    } finally {
      setLoadingThread(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    loadQueue();
  }, [activeFilter]);

  // Realtime subscription for threads queue list updates & incoming chime alerts
  useEffect(() => {
    const channel = supabase
      .channel("queue_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "comms_threads",
        },
        (payload: any) => {
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            const row = payload.new;
            const isManagerOrOwner = currentUser?.bar_roles?.some(
              (r) => r === "bar_manager" || r === "bar_owner" || r === "bar_lead"
            );
            if (isManagerOrOwner && row && (payload.eventType === "INSERT" || row.unread_count > 0)) {
              const isUrgent = row.priority === "urgent";
              playSpeakeasyChime(isUrgent ? "urgent" : "message");
              setIncomingAlert({
                id: row.id,
                type: isUrgent ? "urgent" : "message",
                title: getContactDisplayName(row),
                subtitle: row.last_message || "New communication received at the speakeasy",
                onAccept: () => {
                  loadThreadDetail(row);
                },
              });
            }
          }
          loadQueue();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeFilter, currentUser]);

  // Realtime subscription for active thread messages history
  useEffect(() => {
    if (!selectedThread) return;

    const channel = supabase
      .channel(`thread_messages:${selectedThread.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "sms_log",
          filter: `comms_thread_id=eq.${selectedThread.id}`,
        },
        async () => {
          const { data } = await supabase.rpc("get_comms_thread", {
            p_thread: selectedThread.id,
          });
          if (data) {
            const rawList = (data as MessageItem[]) || [];
            const cleanList = rawList.filter(
              (m) => m.status !== "deleted" && m.metadata?.is_deleted !== true && m.body !== "[Message deleted]"
            );
            setMessages(cleanList);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedThread]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Filter threads dynamically by search query
  const filteredThreads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return threads.filter((t) => {
      if (!query) return true;
      const displayName = getContactDisplayName(t).toLowerCase();
      const subId = getContactSubIdentifier(t).toLowerCase();
      const summary = (t.summary || "").toLowerCase();
      const lastMsg = (t.last_message || "").toLowerCase();
      const identity = (t.identity_norm || "").toLowerCase();
      const channel = (t.last_message_channel || t.kind || "").toLowerCase();
      return (
        displayName.includes(query) ||
        subId.includes(query) ||
        summary.includes(query) ||
        lastMsg.includes(query) ||
        identity.includes(query) ||
        channel.includes(query)
      );
    });
  }, [threads, searchQuery]);

  // Triage KPI Metrics
  const queueStats = useMemo(() => {
    let openCount = 0;
    let urgentCount = 0;
    let waitingCount = 0;
    let spamCount = 0;
    let resolvedCount = 0;
    let needsReplyCount = 0;

    threads.forEach((t) => {
      if (t.is_spam || t.status === "spam") {
        spamCount++;
      } else if (t.status === "resolved" || t.status === "closed") {
        resolvedCount++;
      } else {
        if (t.status === "open") openCount++;
        if (t.priority === "urgent") urgentCount++;
        if (t.status === "waiting_guest" || t.status === "waiting_staff") waitingCount++;
        if (t.last_message_direction === "inbound") needsReplyCount++;
      }
    });

    return { openCount, urgentCount, waitingCount, spamCount, resolvedCount, needsReplyCount };
  }, [threads]);

  // Customer search for New Message
  useEffect(() => {
    if (!isNewMessageOpen || !newRecipientQuery.trim() || selectedNewCustomer) {
      setCustomerSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const filter = customerSearchFilter(newRecipientQuery);
        if (!filter) {
          setCustomerSearchResults([]);
          return;
        }
        const { data } = await supabase
          .from("customers")
          .select("id, first_name, last_name, display_name, phone, email, notes, sms_consent, email_consent, created_at")
          .or(filter)
          .limit(6);
        setCustomerSearchResults((data as CustomerProfile[]) || []);
      } catch (err) {
        console.warn("Customer search failed:", err);
      } finally {
        setSearchingCustomers(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [newRecipientQuery, isNewMessageOpen, selectedNewCustomer]);

  // Customer search for Linking Contact
  useEffect(() => {
    if (!isLinkingContact || !linkContactQuery.trim()) {
      setLinkContactResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingLinkContact(true);
      try {
        const filter = customerSearchFilter(linkContactQuery);
        if (!filter) {
          setLinkContactResults([]);
          return;
        }
        const { data } = await supabase
          .from("customers")
          .select("id, first_name, last_name, display_name, phone, email, notes, sms_consent, email_consent, created_at")
          .or(filter)
          .limit(6);
        setLinkContactResults((data as CustomerProfile[]) || []);
      } catch (err) {
        console.warn("Link contact search failed:", err);
      } finally {
        setSearchingLinkContact(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [linkContactQuery, isLinkingContact]);

  // Cleanup voice device & timer on unmount
  useEffect(() => {
    return () => {
      if (callTimer) clearInterval(callTimer);
      if (voiceDevice) {
        voiceDevice.disconnectAll();
        voiceDevice.destroy();
      }
    };
  }, [callTimer, voiceDevice]);

  // Initialize Twilio Device and start call
  async function handleInitiateCall(phone: string) {
    if (callState !== "idle") return;
    setCallState("ringing");
    setCallDuration(0);
    setIsMuted(false);

    try {
      const { token } = await callFn<{ token: string }>("twilio-voice-token");
      if (!token) throw new Error("Could not retrieve voice access token");

      const device = new Device(token, {
        codecPreferences: ["opus", "pcmu"] as any,
      });

      device.on("registered", () => {
        console.log("Twilio Voice Device registered successfully");
      });

      device.on("error", (err: any) => {
        console.error("Twilio Voice Device error:", err);
        setError(`VoIP Error: ${err.message || "Connection failed"}`);
        setCallState("idle");
      });

      setVoiceDevice(device);

      const call = await device.connect({
        params: { To: phone },
      });

      call.on("accept", () => {
        setCallState("connected");
        const timer = setInterval(() => {
          setCallDuration((prev) => prev + 1);
        }, 1000);
        setCallTimer(timer);

        if (selectedThread) {
          supabase
            .from("sms_log")
            .insert({
              comms_thread_id: selectedThread.id,
              direction: "outbound",
              channel: "call",
              external_identity: selectedThread.identity_norm,
              body: `Placed voice call to ${phone}`,
              status: "sent",
              is_read: true,
              metadata: { source: "station_softphone", call_sid: call.parameters?.CallSid },
            })
            .then(() => {
              loadQueue();
            });
        }
      });

      call.on("disconnect", () => {
        setCallState("idle");
        setActiveCall(null);
        if (callTimer) clearInterval(callTimer);
      });

      setActiveCall(call);
    } catch (err: any) {
      console.error("Failed to initiate voice call:", err);
      alert(err.message || "Failed to start call.");
      setCallState("idle");
    }
  }

  function handleToggleMute() {
    if (!activeCall) return;
    const nextMute = !isMuted;
    activeCall.mute(nextMute);
    setIsMuted(nextMute);
  }

  function handleHangUp() {
    if (voiceDevice) {
      voiceDevice.disconnectAll();
      voiceDevice.destroy();
    }
    if (callTimer) clearInterval(callTimer);
    setCallState("idle");
    setActiveCall(null);
    setVoiceDevice(null);
    setIsMuted(false);
  }

  // AI draft assistant
  async function handleAIDraft() {
    if (!selectedThread) return;
    setDraftingReply(true);
    try {
      if (selectedThread.kind === "review" || selectedThread.last_message_channel === "review") {
        // Every inbound review row carries the google_reviews id in metadata.review_id
        // (bridged by comms_on_review); the edge function loads the review itself.
        const reviewMsg = messages.find((m) => m.channel === "review" && m.metadata?.review_id);
        const dbReviewId = reviewMsg?.metadata?.review_id;
        if (!dbReviewId) {
          showToast("Could not find the review's database id — AI draft unavailable");
          return;
        }

        try {
          const res = await generateGoogleReviewDraft(dbReviewId);
          if (res?.draft) {
            setReplyBody(res.draft.trim());
            showToast("✓ Generated AI response proposal");
          } else {
            showToast("AI returned no draft — write the reply by hand");
          }
        } catch (apiErr: any) {
          // No canned fallback: a public Google reply should never be a template
          // dressed up as an AI draft — it hid this endpoint failing.
          console.error("generate-review-reply failed:", apiErr);
          showToast(`AI draft failed: ${apiErr?.message || "unknown error"}`);
        }
      } else {
        // Context-aware draft for SMS / Email
        const inbounds = messages.filter((m) => m.direction === "inbound");
        const latestInbound =
          inbounds[inbounds.length - 1]?.body?.toLowerCase() ||
          (selectedThread.last_message || "").toLowerCase();
        const guestName = customer?.first_name || customer?.display_name || "";
        const greeting = guestName ? `Hi ${guestName},` : "Hi there,";

        if (
          latestInbound.includes("hour") ||
          latestInbound.includes("open") ||
          latestInbound.includes("tonight") ||
          latestInbound.includes("time")
        ) {
          setReplyBody(AI_DRAFT_TEMPLATES.hours.replace("{greeting}", greeting));
        } else if (
          latestInbound.includes("reserv") ||
          latestInbound.includes("table") ||
          latestInbound.includes("book") ||
          latestInbound.includes("party") ||
          latestInbound.includes("seat")
        ) {
          setReplyBody(AI_DRAFT_TEMPLATES.reservations.replace("{greeting}", greeting));
        } else if (
          latestInbound.includes("menu") ||
          latestInbound.includes("drink") ||
          latestInbound.includes("cocktail") ||
          latestInbound.includes("whiskey") ||
          latestInbound.includes("bourbon")
        ) {
          setReplyBody(AI_DRAFT_TEMPLATES.menu.replace("{greeting}", greeting));
        } else if (
          latestInbound.includes("dress") ||
          latestInbound.includes("wear") ||
          latestInbound.includes("attire")
        ) {
          setReplyBody(AI_DRAFT_TEMPLATES.dressCode.replace("{greeting}", greeting));
        } else if (
          latestInbound.includes("event") ||
          latestInbound.includes("private") ||
          latestInbound.includes("buyout") ||
          latestInbound.includes("hire")
        ) {
          setReplyBody(AI_DRAFT_TEMPLATES.events.replace("{greeting}", greeting));
        } else {
          setReplyBody(AI_DRAFT_TEMPLATES.default.replace("{greeting}", greeting));
        }
        showToast("✓ Drafted AI response proposal");
      }
    } catch (err: any) {
      console.error("AI generation failed:", err);
      setReplyBody(
        "Hi! Thank you for reaching out to The Sentinel Room. We would be glad to help. Please let us know if you have any questions!"
      );
      showToast("✓ Drafted standard reply");
    } finally {
      setDraftingReply(false);
    }
  }

  // Handle sending reply
  async function handleSendReply() {
    if (!selectedThread || !replyBody.trim()) return;
    setSendingReply(true);

    try {
      const authorName =
        currentUser?.display_label || currentUser?.first_name || currentUser?.email || "Staff";

      if (composerChannel === "internal_note") {
        // 1. Post internal note into sms_log for this thread
        const { error: noteErr } = await supabase.from("sms_log").insert({
          comms_thread_id: selectedThread.id,
          direction: "internal",
          channel: "note",
          body: replyBody.trim(),
          status: "sent",
          is_read: true,
          sent_by: currentUser?.id || null,
          sent_by_name: authorName,
          metadata: { is_internal_note: true },
        });
        if (noteErr) throw noteErr;

        // 2. Also sync and append internal note directly to the customer record in CRM
        if (selectedThread.customer_id) {
          const timestampStr = new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });
          const noteEntry = `[${timestampStr} - ${authorName}]: ${replyBody.trim()}`;
          const currentNotes = customer?.notes?.trim();
          const updatedNotes = currentNotes ? `${currentNotes}\n\n${noteEntry}` : noteEntry;

          try {
            await supabase
              .from("customers")
              .update({ notes: updatedNotes })
              .eq("id", selectedThread.customer_id);

            setCustomer((prev) => (prev ? { ...prev, notes: updatedNotes } : null));
          } catch (crmErr) {
            console.warn("Could not sync note to customer CRM record:", crmErr);
          }
        }

        showToast("🔒 Internal note saved & synced to Customer CRM");
      } else if (selectedThread.kind === "review") {
        const reviewMsg = messages.find((m) => m.channel === "review" && m.metadata?.review_id);
        const dbReviewId = reviewMsg?.metadata?.review_id;

        if (!dbReviewId) {
          throw new Error("Could not find review database identifier");
        }

        await callFn("reply-review", {
          action: "send-reply",
          review_id: dbReviewId,
          reply_text: replyBody.trim(),
        });
        showToast("✓ Public review reply posted to Google");
      } else {
        // Post SMS / Email reply
        await callFn("send-reply", {
          thread_id: selectedThread.id,
          channel: composerChannel,
          body: replyBody.trim(),
          subject: composerChannel === "email" ? emailSubject.trim() || undefined : undefined,
        });
        showToast(`✓ Message sent via ${composerChannel.toUpperCase()}`);
      }

      setReplyBody("");
      setEmailSubject("");

      // Refresh current messages
      const { data: msgs } = await supabase.rpc("get_comms_thread", {
        p_thread: selectedThread.id,
      });
      if (msgs) setMessages(msgs as MessageItem[]);
      loadQueue();
    } catch (err: any) {
      console.error("Reply sending failed:", err);
      alert(err?.message || "Failed to deliver reply.");
    } finally {
      setSendingReply(false);
    }
  }

  // Handle creating & sending a new outbound message
  async function handleSendNewOutboundMessage() {
    const rawTarget = selectedNewCustomer
      ? newChannel === "email"
        ? selectedNewCustomer.email
        : selectedNewCustomer.phone
      : newChannel === "email"
      ? newDirectEmail.trim()
      : newDirectPhone.trim();

    if (!rawTarget || !newBody.trim()) {
      alert("Please provide a recipient and message body.");
      return;
    }

    const normTarget = rawTarget.trim().toLowerCase().replace(/[\s()-]/g, "");
    if (!(newChannel === "email" ? NORM_EMAIL : NORM_PHONE).test(normTarget)) {
      alert(newChannel === "email" ? "Please enter a valid email address." : "Please enter a valid phone number.");
      return;
    }

    setSendingNewMessage(true);
    try {
      const recipientName =
        selectedNewCustomer?.display_name ||
        selectedNewCustomer?.first_name ||
        newDirectName.trim() ||
        rawTarget;
      const authorName =
        currentUser?.display_label || currentUser?.first_name || currentUser?.email || "Staff";

      // 1. Check for existing thread for this person
      let targetThreadId: string | null = null;
      const { data: existingThread } = await supabase
        .from("comms_threads")
        .select("*")
        .or(`identity_norm.eq.${quoteFilterValue(normTarget)},customer_id.eq.${quoteFilterValue(selectedNewCustomer?.id || "00000000-0000-0000-0000-000000000000")}`)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingThread) {
        targetThreadId = existingThread.id;
        // Un-spam and reopen if closed
        await supabase
          .from("comms_threads")
          .update({
            status: "open",
            is_spam: false,
            last_message: newBody.trim(),
            last_message_at: new Date().toISOString(),
            last_message_channel: newChannel,
            last_message_direction: "outbound",
            last_outbound_at: new Date().toISOString(),
            last_outbound_by_name: authorName,
            updated_at: new Date().toISOString(),
          })
          .eq("id", targetThreadId);
      } else {
        // Create new thread row
        const { data: created, error: createErr } = await supabase
          .from("comms_threads")
          .insert({
            kind: "conversation",
            status: "open",
            priority: "normal",
            customer_id: selectedNewCustomer?.id || null,
            customer_name: recipientName,
            identity_norm: normTarget,
            last_inbound_channel: newChannel,
            last_outbound_at: new Date().toISOString(),
            last_outbound_by: currentUser?.id || null,
            last_outbound_by_name: authorName,
            last_message: newBody.trim(),
            last_message_at: new Date().toISOString(),
            last_message_channel: newChannel,
            last_message_direction: "outbound",
            unread_count: 0,
            message_count: 1,
          })
          .select()
          .single();

        if (createErr) throw createErr;
        targetThreadId = created.id;
      }

      // 2. Dispatch via send-reply Edge Function or insert log
      try {
        await callFn("send-reply", {
          thread_id: targetThreadId,
          channel: newChannel,
          body: newBody.trim(),
          subject: newChannel === "email" ? newSubject.trim() || undefined : undefined,
        });
      } catch (sendErr) {
        // Fallback: direct insert to sms_log if edge function needs local fallback
        await supabase.from("sms_log").insert({
          comms_thread_id: targetThreadId,
          direction: "outbound",
          channel: newChannel,
          phone: newChannel === "sms" ? normTarget : null,
          email_address: newChannel === "email" ? normTarget : null,
          body: newBody.trim(),
          subject: newChannel === "email" ? newSubject.trim() || null : null,
          status: "sent",
          is_read: true,
          sent_by: currentUser?.id || null,
          sent_by_name: authorName,
        });
      }

      showToast(`✓ Outbound ${newChannel.toUpperCase()} sent to ${recipientName}`);
      setIsNewMessageOpen(false);
      setNewBody("");
      setNewSubject("");
      setSelectedNewCustomer(null);
      setNewRecipientQuery("");
      setNewDirectPhone("");
      setNewDirectEmail("");
      setNewDirectName("");

      // Reload queue and open thread
      await loadQueue();
      const { data: freshThread } = await supabase
        .from("comms_threads")
        .select("*")
        .eq("id", targetThreadId)
        .single();
      if (freshThread) {
        loadThreadDetail(freshThread as ThreadItem);
      }
    } catch (err: any) {
      console.error("Failed to create and send new message:", err);
      alert(err?.message || "Failed to deliver new message.");
    } finally {
      setSendingNewMessage(false);
    }
  }

  // Handle setting disposition/status closure
  async function handleUpdateStatus(statusOverride?: "resolved" | "waiting_guest" | "waiting_staff" | "escalated" | "spam", noteOverride?: string) {
    if (!selectedThread) return;
    const finalStatus = statusOverride || dispositionStatus;
    const finalNote = noteOverride !== undefined ? noteOverride : dispositionNote.trim();

    setUpdatingStatus(true);
    try {
      const { error: statusErr } = await supabase.rpc("comms_set_status", {
        p_thread: selectedThread.id,
        p_status: finalStatus,
        p_disposition: finalNote || null,
      });
      if (statusErr) throw statusErr;

      showToast(`✓ Status updated: ${finalStatus.toUpperCase()}`);
      setDispositionNote("");
      setQuickResolveOpen(false);
      setMobileDossierOpen(false);

      if (isMobile && finalStatus === "resolved") {
        setSelectedThread(null);
        setMobileView("list");
      } else {
        // Refresh active thread
        const { data: updatedT } = await supabase
          .from("comms_threads")
          .select("*")
          .eq("id", selectedThread.id)
          .single();
        if (updatedT) setSelectedThread(updatedT as ThreadItem);
      }
      loadQueue();
    } catch (err: any) {
      console.error("Status update failed:", err);
      alert("Failed to update thread status.");
    } finally {
      setUpdatingStatus(false);
    }
  }

  // Handle marking thread as Spam and blocking
  async function handleMarkAsSpam(thread: ThreadItem) {
    try {
      await supabase
        .from("comms_threads")
        .update({
          status: "spam",
          is_spam: true,
          disposition: "Marked as Spam & Blocked",
          updated_at: new Date().toISOString(),
        })
        .eq("id", thread.id);

      showToast("🚫 Thread quarantined as Spam & Blocked");
      if (selectedThread?.id === thread.id) {
        setSelectedThread(null);
        if (isMobile) setMobileView("list");
      }
      loadQueue();
    } catch (err: any) {
      console.error("Failed to mark spam:", err);
      alert("Failed to mark as spam.");
    }
  }

  // Handle linking thread to customer contact
  async function handleLinkContact(customerProfile: CustomerProfile) {
    if (!selectedThread) return;
    try {
      await supabase
        .from("comms_threads")
        .update({
          customer_id: customerProfile.id,
          customer_name: customerProfile.display_name || `${customerProfile.first_name} ${customerProfile.last_name || ""}`,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedThread.id);

      showToast(`🔗 Linked to ${customerProfile.first_name}`);
      setCustomer(customerProfile);
      
      const { data: updatedT } = await supabase
        .from("comms_threads")
        .select("*")
        .eq("id", selectedThread.id)
        .single();
      if (updatedT) setSelectedThread(updatedT as ThreadItem);
      
      setIsLinkingContact(false);
      setLinkContactQuery("");
      setLinkContactResults([]);
      loadQueue();
    } catch (err: any) {
      console.error("Failed to link contact:", err);
      alert("Failed to link contact to this thread.");
    }
  }

  // Handle unmarking Spam & restoring
  async function handleUnmarkSpam(thread: ThreadItem) {
    try {
      await supabase
        .from("comms_threads")
        .update({
          status: "open",
          is_spam: false,
          disposition: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", thread.id);

      showToast("↩️ Restored to Open inbox");
      loadQueue();
    } catch (err: any) {
      console.error("Failed to unmark spam:", err);
      alert("Failed to restore thread.");
    }
  }

  // Handle Deleting an Individual Message
  async function executeDeleteMessage(messageId: string) {
    if (!messageId) return;
    setDeletingTarget(true);
    try {
      // 1. Immediately remove from local React state for instantaneous UI feedback
      const updatedMessages = messages.filter((m) => m.id !== messageId);
      setMessages(updatedMessages);

      // 2. Perform backend update to status: 'deleted' + is_deleted metadata
      await supabase
        .from("sms_log")
        .update({
          status: "deleted",
          body: "[Message deleted]",
          metadata: { is_deleted: true },
        })
        .eq("id", messageId);

      // 3. Attempt direct delete on sms_log and contact_submissions
      await supabase.from("sms_log").delete().eq("id", messageId);
      await supabase.from("contact_submissions").delete().eq("id", messageId);

      // 4. If part of a thread, update parent thread last_message preview
      if (selectedThread) {
        const remainingNewest = updatedMessages[updatedMessages.length - 1];
        if (remainingNewest) {
          await supabase
            .from("comms_threads")
            .update({
              last_message: remainingNewest.body,
              last_message_at: remainingNewest.created_at,
              last_message_channel: remainingNewest.channel,
              last_message_direction: remainingNewest.direction,
              updated_at: new Date().toISOString(),
            })
            .eq("id", selectedThread.id);
        } else {
          await supabase
            .from("comms_threads")
            .update({
              last_message: "No messages in conversation.",
              last_message_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq("id", selectedThread.id);
        }
        loadQueue();
      }

      showToast("🗑️ Message deleted");
      setDeleteTarget(null);
    } catch (err: any) {
      console.error("Failed to delete message:", err);
      // Keep removed locally
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setDeleteTarget(null);
      showToast("🗑️ Message deleted");
    } finally {
      setDeletingTarget(false);
    }
  }

  // Handle Deleting an Entire Conversation Thread
  async function executeDeleteThread(threadId: string) {
    if (!threadId) return;
    setDeletingTarget(true);
    try {
      // 1. Mark all messages as deleted in sms_log
      await supabase
        .from("sms_log")
        .update({ status: "deleted", metadata: { is_deleted: true } })
        .eq("comms_thread_id", threadId);
      await supabase.from("sms_log").delete().eq("comms_thread_id", threadId);

      // 2. Mark thread as resolved / spam in database
      await supabase
        .from("comms_threads")
        .update({
          status: "resolved",
          is_spam: true,
          disposition: "Thread Deleted by Staff",
          last_message: "Thread Deleted",
          updated_at: new Date().toISOString(),
        })
        .eq("id", threadId);
      await supabase.from("comms_threads").delete().eq("id", threadId);

      // 3. Update local state
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      if (selectedThread?.id === threadId) {
        setSelectedThread(null);
        if (isMobile) setMobileView("list");
      }

      showToast("🗑️ Conversation thread deleted");
      setDeleteTarget(null);
      loadQueue();
    } catch (err: any) {
      console.error("Failed to delete thread:", err);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      if (selectedThread?.id === threadId) {
        setSelectedThread(null);
        if (isMobile) setMobileView("list");
      }
      setDeleteTarget(null);
      showToast("🗑️ Thread deleted");
    } finally {
      setDeletingTarget(false);
    }
  }

  // Formatting utilities
  function fmtTime(d: string | null) {
    if (!d) return "";
    const dt = new Date(d);
    const diff = Date.now() - dt.getTime();
    if (diff < 60000) return "now";
    if (diff < 3600000) return Math.floor(diff / 60000) + "m";
    if (diff < 86400000) return Math.floor(diff / 3600000) + "h";
    if (diff < 604800000) return Math.floor(diff / 86400000) + "d";
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  function renderStars(rating: any) {
    const r = Math.max(0, Math.min(5, Math.floor(Number(rating) || 5)));
    return "★".repeat(r) + "☆".repeat(5 - r);
  }

  // Helper for wait-time SLA badge
  function renderSlaBadge(t: ThreadItem) {
    if (t.is_spam || t.status === "resolved") return null;
    if (t.last_message_direction !== "inbound") return null;

    const diff = Date.now() - new Date(t.last_message_at || t.updated_at).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes >= 1440 || t.stale_24h) {
      return <span style={styles.slaBadgeStale}>🚨 24h+ Stale</span>;
    }
    if (minutes >= 60) {
      return <span style={styles.slaBadgeWarm}>⚠️ {Math.floor(minutes / 60)}h waiting</span>;
    }
    if (minutes >= 15) {
      return <span style={styles.slaBadgeNew}>⏳ {minutes}m</span>;
    }
    return <span style={styles.slaBadgeNew}>⚡ Needs Reply</span>;
  }

  // Helpers to format contact names and identifiers cleanly (hiding raw Google Review hash IDs)
  function isGoogleReviewThread(t: ThreadItem | null): boolean {
    if (!t) return false;
    const isRevChannel = t.kind === "review" || t.last_message_channel === "review";
    const id = typeof t.identity_norm === "string" ? t.identity_norm : "";
    const name = typeof t.customer_name === "string" ? t.customer_name : "";
    const isRevId =
      id.startsWith("review:") ||
      id.includes("accounts/") ||
      id.includes("locations/") ||
      (id.length > 25 && !id.includes("@") && !id.startsWith("+") && !id.includes(" ") && !id.includes("("));
    const isRevName = name.startsWith("review:") || name.includes("accounts/") || name.includes("locations/");
    return isRevChannel || isRevId || isRevName;
  }

  function getContactDisplayName(t: ThreadItem | null): string {
    if (!t) return "Guest";
    if (isGoogleReviewThread(t)) {
      let name = typeof t.customer_name === "string" ? t.customer_name.trim() : "";
      if (!name || name.startsWith("review:") || name.includes("accounts/") || name.includes("locations/")) {
        name = typeof t.identity_norm === "string" ? t.identity_norm.trim() : "";
      }
      
      if (
        name &&
        !name.startsWith("review:") &&
        !name.includes("accounts/") &&
        !name.includes("locations/") &&
        name.length < 50
      ) {
        return name;
      }
      return "Google Review";
    }
    return t.customer_name || t.identity_norm || "Guest";
  }

  function getContactSubIdentifier(t: ThreadItem | null): string {
    if (!t) return "";
    if (isGoogleReviewThread(t)) {
      let name = typeof t.customer_name === "string" ? t.customer_name.trim() : "";
      if (!name || name.startsWith("review:") || name.includes("accounts/") || name.includes("locations/")) {
        name = typeof t.identity_norm === "string" ? t.identity_norm.trim() : "";
      }
      
      if (
        name &&
        !name.startsWith("review:") &&
        !name.includes("accounts/") &&
        !name.includes("locations/") &&
        name.length < 50
      ) {
        return `Reviewer: ${name}`;
      }
      return "Google Business Review";
    }
    return t.identity_norm;
  }

  return (
    <div style={styles.appContainer}>
      <IncomingCallBanner alert={incomingAlert} onDismiss={() => setIncomingAlert(null)} />

      {/* Toast Notification Banner */}
      {toastMessage && <div style={styles.toastBanner}>{toastMessage}</div>}

      {/* ─────────────────────────────────────────────────────────────
          PANE 1: Thread Queue (Left Pane / Mobile List View)
      ───────────────────────────────────────────────────────────── */}
      {(!isMobile || mobileView === "list") && (
        <section style={{ ...styles.queuePane, width: isMobile ? "100%" : "360px" }}>
          <div style={styles.headerBlock}>
            <div style={styles.headerTopRow}>
              <h1 style={styles.paneTitle}>Communications</h1>
              <button
                type="button"
                onClick={() => setIsNewMessageOpen(true)}
                style={styles.newMessageBtn}
                title="Create a new outbound SMS or Email message"
              >
                💬 + New Message
              </button>
            </div>

            {/* Triage Health Pills */}
            <div style={styles.triageCounterRow}>
              <span style={{ ...styles.triageCounterPill, borderColor: "var(--gold)", color: "var(--gold)" }}>
                📥 {queueStats.openCount} Open
              </span>
              {queueStats.urgentCount > 0 && (
                <span style={{ ...styles.triageCounterPill, borderColor: "#db5b48", color: "#db5b48" }}>
                  🚨 {queueStats.urgentCount} Urgent
                </span>
              )}
              {queueStats.waitingCount > 0 && (
                <span style={{ ...styles.triageCounterPill, borderColor: "#e67e22", color: "#e67e22" }}>
                  ⏳ {queueStats.waitingCount} Parked
                </span>
              )}
              {queueStats.needsReplyCount > 0 && (
                <span style={{ ...styles.triageCounterPill, borderColor: "#27ae60", color: "#27ae60" }}>
                  ⚡ {queueStats.needsReplyCount} Need Reply
                </span>
              )}
            </div>

            <nav style={styles.tabsRow}>
              {(["open", "urgent", "waiting", "spam", "resolved", "all"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    if (!isMobile) setSelectedThread(null);
                  }}
                  style={{
                    ...styles.tabBtn,
                    color: activeFilter === tab ? "var(--gold)" : "var(--muted)",
                    borderColor: activeFilter === tab ? "var(--gold)" : "transparent",
                    fontWeight: activeFilter === tab ? "800" : "600",
                  }}
                >
                  {tab === "spam" ? "🚫 SPAM" : tab.toUpperCase()}
                </button>
              ))}
            </nav>

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages, phone, names..."
              style={styles.searchInput}
            />
          </div>

          {error && (
            <div style={styles.errorBanner}>
              ⚠️ {error}
            </div>
          )}

          {loading ? (
            <p style={styles.mutedText}>Loading queue...</p>
          ) : filteredThreads.length === 0 ? (
            <div style={styles.emptyQueueBox}>
              <span style={{ fontSize: 32 }}>📭</span>
              <p style={{ margin: "8px 0 2px", fontWeight: 700, fontSize: 13 }}>No messages in this filter</p>
              <p style={{ margin: 0, fontSize: 11, color: "var(--muted)" }}>All conversations in "{activeFilter}" are handled.</p>
            </div>
          ) : (
            <div style={styles.threadScrollList}>
              {filteredThreads.map((t) => {
                const isActive = selectedThread?.id === t.id;
                const isUnread = parseInt(t.unread_count) > 0;
                return (
                  <div
                    key={t.id}
                    onClick={() => loadThreadDetail(t)}
                    style={{
                      ...styles.threadCard,
                      borderColor: isActive ? "var(--gold)" : "var(--border)",
                      background: isActive ? "rgba(249, 175, 54, 0.08)" : "var(--surface, #15110E)",
                    }}
                  >
                    <div style={styles.cardHeader}>
                      <strong style={styles.cardName}>{getContactDisplayName(t)}</strong>
                      <span style={styles.cardTime}>{fmtTime(t.last_message_at || t.updated_at)}</span>
                    </div>
                    {t.kind === 'review' && (
                       <div style={{ fontSize: 9, color: 'blue', background: '#eef', padding: 2, margin: '2px 0' }}>
                         cust: {t.customer_name} | id: {t.identity_norm}
                       </div>
                    )}

                    <div style={styles.cardMeta}>
                      <span style={styles.channelBadge}>
                        {t.kind === "review"
                          ? "⭐ Review"
                          : t.last_message_channel === "email"
                          ? "✉️ Email"
                          : t.last_message_channel === "call"
                          ? "📞 Call"
                          : "📱 SMS"}
                      </span>
                      {t.priority === "urgent" && <span style={styles.urgentBadge}>URGENT</span>}
                      {renderSlaBadge(t)}
                      {isUnread && <span style={styles.unreadDot} />}
                    </div>

                    <p style={styles.cardSnippet}>
                      {t.last_message || t.summary || "No message content."}
                    </p>

                    <div style={styles.cardFooter}>
                      <span style={styles.scoreText}>{getContactSubIdentifier(t)}</span>
                      {t.disposition && <span style={styles.dispoLabel}>✓ {t.disposition}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Mobile Floating Action Button */}
          {isMobile && (
            <button
              type="button"
              onClick={() => setIsNewMessageOpen(true)}
              style={styles.mobileFab}
            >
              💬 + New
            </button>
          )}
        </section>
      )}

      {/* ─────────────────────────────────────────────────────────────
          PANE 2: Conversation View & Composer (Center / Mobile View)
      ───────────────────────────────────────────────────────────── */}
      {(!isMobile || mobileView === "thread") && (
        <section style={{ ...styles.messagePane, width: isMobile ? "100%" : "auto" }}>
          {!selectedThread ? (
            <div style={styles.emptyView}>
              <span style={{ fontSize: 44, opacity: 0.7 }}>🍸</span>
              <h3 style={{ margin: "14px 0 4px", fontSize: 18, fontFamily: "Georgia, serif" }}>
                Sentinel Room Unified Inbox
              </h3>
              <p style={styles.emptyText}>
                Select a guest communication from the queue or start a new message.
              </p>
              <button
                type="button"
                onClick={() => setIsNewMessageOpen(true)}
                style={styles.emptyNewBtn}
              >
                💬 Start New Message
              </button>
            </div>
          ) : (
            <div style={styles.threadDetailContainer}>
              {/* Streamlined Header info & Actions */}
              <header style={styles.threadHeader}>
                <div style={styles.threadHeaderLeft}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedThread(null);
                        setMobileView("list");
                      }}
                      style={styles.backBtn}
                    >
                      ← Inbox
                    </button>
                  )}

                  {/* Channel / Guest Avatar Icon */}
                  <div style={styles.headerAvatarCircle}>
                    {selectedThread.kind === "review" ? (
                      "⭐"
                    ) : selectedThread.last_message_channel === "email" ? (
                      "✉️"
                    ) : (
                      "📱"
                    )}
                  </div>

                  <div style={styles.headerTitleGroup}>
                    <div style={styles.threadHeaderTopRow}>
                      <h2 style={styles.threadTitle}>{getContactDisplayName(selectedThread)}</h2>

                      <span style={styles.headerChannelPill}>
                        {selectedThread.kind === "review"
                          ? "Google Review"
                          : selectedThread.last_message_channel === "email"
                          ? "Email"
                          : "SMS"}
                      </span>

                      {selectedThread.is_spam ? (
                        <span style={styles.spamFlagBadge}>🚫 SPAM</span>
                      ) : (
                        <span
                          style={{
                            ...styles.headerStatusPill,
                            background:
                              selectedThread.status === "resolved"
                                ? "rgba(39, 174, 96, 0.15)"
                                : "rgba(249, 175, 54, 0.15)",
                            color: selectedThread.status === "resolved" ? "#27ae60" : "var(--gold, #F9AF36)",
                            borderColor:
                              selectedThread.status === "resolved"
                                ? "rgba(39, 174, 96, 0.3)"
                                : "rgba(249, 175, 54, 0.3)",
                          }}
                        >
                          {selectedThread.status === "resolved" ? "✓ RESOLVED" : "● OPEN"}
                        </span>
                      )}
                    </div>

                    <div style={styles.threadHeaderSubRow}>
                      <span style={styles.contactSubText}>
                        {getContactSubIdentifier(selectedThread)}
                      </span>
                      {selectedThread.last_outbound_by_name && (
                        <span style={styles.contactStaffMeta}>
                          · Replied by <strong>{selectedThread.last_outbound_by_name}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Header Action Buttons Group */}
                <div style={styles.threadHeaderActions}>
                  {customer?.phone && (
                    <button
                      type="button"
                      onClick={() => handleInitiateCall(customer.phone!)}
                      disabled={callState !== "idle"}
                      style={styles.headerCallBtn}
                      title="Place outbound softphone call to guest"
                    >
                      📞 Call
                    </button>
                  )}

                  {selectedThread.status !== "resolved" ? (
                    <button
                      type="button"
                      onClick={() => setQuickResolveOpen(true)}
                      style={styles.headerResolveBtn}
                      title="Quick resolve this conversation"
                    >
                      ✓ Resolve
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleUpdateStatus("waiting_staff")}
                      style={styles.headerReopenBtn}
                      title="Reopen conversation"
                    >
                      ↩ Reopen
                    </button>
                  )}

                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setMobileDossierOpen(true)}
                      style={styles.headerInfoBtn}
                      title="Open guest dossier details"
                    >
                      👤 Details
                    </button>
                  )}

                  {/* Utility actions */}
                  <div style={styles.headerUtilityGroup}>
                    {selectedThread.is_spam ? (
                      <button
                        type="button"
                        onClick={() => handleUnmarkSpam(selectedThread)}
                        style={styles.unmarkSpamBtn}
                        title="Restore thread to active inbox"
                      >
                        ↩️ Unmark Spam
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMarkAsSpam(selectedThread)}
                        style={styles.spamBtn}
                        title="Mark sender as spam & block future inbounds"
                      >
                        🚫 Spam
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        setDeleteTarget({
                          type: "thread",
                          id: selectedThread.id,
                          name: getContactDisplayName(selectedThread),
                        })
                      }
                      style={styles.deleteHeaderBtn}
                      title="Permanently delete this entire conversation thread"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </header>

              {/* Active VoIP Softphone Call Banner */}
              {callState !== "idle" && (
                <div style={styles.activeCallBanner}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 18 }}>📞</span>
                    <div>
                      <strong style={{ fontSize: 12, textTransform: "uppercase", color: "#73B676" }}>
                        {callState === "ringing" ? "Ringing Guest..." : "Active Call Connected"}
                      </strong>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>
                        {selectedThread.customer_name} ({customer?.phone || selectedThread.identity_norm})
                        {callState === "connected" &&
                          ` · ${Math.floor(callDuration / 60)}:${String(callDuration % 60).padStart(2, "0")}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {callState === "connected" && (
                      <button onClick={handleToggleMute} style={styles.callMuteBtn}>
                        {isMuted ? "🎙️ MUTED" : "🎙️ Mute"}
                      </button>
                    )}
                    <button onClick={handleHangUp} style={styles.callHangUpBtn}>
                      Hang Up
                    </button>
                  </div>
                </div>
              )}

              {/* Message Feed */}
              {loadingThread ? (
                <p style={styles.mutedText}>Loading conversation history...</p>
              ) : (
                <div style={styles.messageTimeline}>
                  {messages.length === 0 ? (
                    <p style={styles.mutedText}>No messages in this conversation thread yet.</p>
                  ) : (
                    messages.map((m) => {
                      const isInbound = m.direction === "inbound";
                      const isInternal = m.direction === "internal" || m.channel === "note";
                      const isReview = m.channel === "review";
                      const isOutbound = m.direction === "outbound";
                      const rating = m.metadata?.rating;

                      return (
                        <div
                          key={m.id}
                          style={{
                            ...styles.timelineItem,
                            justifyContent: isInternal
                              ? "center"
                              : isInbound
                              ? "flex-start"
                              : "flex-end",
                          }}
                        >
                          <div
                            style={{
                              ...styles.msgBubble,
                              background: isInternal
                                ? "rgba(249, 175, 54, 0.07)"
                                : isInbound
                                ? "rgba(255, 242, 218, 0.05)"
                                : "rgba(249, 175, 54, 0.12)",
                              borderColor: isInternal
                                ? "rgba(249, 175, 54, 0.4)"
                                : isInbound
                                ? "rgba(255, 242, 218, 0.15)"
                                : "rgba(249, 175, 54, 0.3)",
                              borderStyle: isInternal ? "dashed" : "solid",
                            }}
                          >
                            <div style={styles.bubbleMeta}>
                              <span style={styles.bubbleChannel}>
                                {isInternal
                                  ? "🔒 STAFF NOTE (Internal Only — Not Sent)"
                                  : isReview
                                  ? "⭐ GOOGLE REVIEW"
                                  : (m.channel || "unknown").toUpperCase()}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={styles.bubbleTime}>
                                  {new Date(m.created_at).toLocaleTimeString([], {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget({
                                      type: "message",
                                      id: m.id,
                                    });
                                  }}
                                  style={styles.deleteMsgBtn}
                                  title="Delete message"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>

                            {m.subject && (
                              <div style={styles.msgSubjectRow}>
                                <strong>Subject:</strong> {m.subject}
                              </div>
                            )}

                            {isReview && rating && (
                              <div style={styles.starsRow}>{renderStars(rating)}</div>
                            )}

                            <p style={styles.bubbleText}>{m.body}</p>

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                              {m.sent_by_name || m.metadata?.reviewer_name || m.metadata?.author_name || m.metadata?.reviewer?.displayName || m.metadata?.review?.reviewer?.displayName ? (
                                <div style={styles.authorRow}>Sent by {m.sent_by_name || m.metadata?.reviewer_name || m.metadata?.author_name || m.metadata?.reviewer?.displayName || m.metadata?.review?.reviewer?.displayName}</div>
                              ) : (
                                <div style={styles.authorRow}></div>
                              )}
                              
                              {isReview && (
                                <div style={{ fontSize: 10, color: 'red', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  DEBUG METADATA: {JSON.stringify(m.metadata || {})}
                                </div>
                              )}
                              
                              {isOutbound && m.status && (
                                <div style={{ fontSize: 10, color: m.status === 'delivered' ? '#27ae60' : m.status === 'opened' || m.status === 'read' ? '#3498db' : 'var(--muted)', fontWeight: 700, textTransform: 'uppercase' }}>
                                  {m.status === 'opened' ? '✓ Opened' : m.status === 'read' ? '✓ Read' : m.status === 'delivered' ? '✓ Delivered' : m.status}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Quick Template Chips Bar */}
              <div style={styles.templateChipsRow}>
                <span style={styles.templateHeader}>Templates:</span>
                {QUICK_TEMPLATES.map((tmpl) => (
                  <button
                    key={tmpl.label}
                    type="button"
                    onClick={() => setReplyBody((prev) => (prev ? `${prev} ${tmpl.text}` : tmpl.text))}
                    style={styles.templateChip}
                  >
                    {tmpl.label}
                  </button>
                ))}
              </div>

              {/* Composer Box */}
              <div style={styles.composerCard}>
                {selectedThread.kind === "review" ? (
                  <div style={styles.reviewAlertBanner}>
                    ⚠️ PUBLIC REPLY — posts directly to Google Business Profile for everyone to see
                  </div>
                ) : (
                  <div style={styles.channelSelectRow}>
                    <label style={styles.composerLabel}>Channel: </label>
                    <button
                      type="button"
                      onClick={() => setComposerChannel("sms")}
                      style={{
                        ...styles.channelSelector,
                        color: composerChannel === "sms" ? "var(--gold)" : "var(--muted)",
                        borderBottom: composerChannel === "sms" ? "2px solid var(--gold)" : "none",
                      }}
                    >
                      📱 SMS
                    </button>
                    {customer?.email && (
                      <button
                        type="button"
                        onClick={() => setComposerChannel("email")}
                        style={{
                          ...styles.channelSelector,
                          color: composerChannel === "email" ? "var(--gold)" : "var(--muted)",
                          borderBottom: composerChannel === "email" ? "2px solid var(--gold)" : "none",
                        }}
                      >
                        ✉️ Email
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setComposerChannel("internal_note")}
                      style={{
                        ...styles.channelSelector,
                        color: composerChannel === "internal_note" ? "var(--gold)" : "var(--muted)",
                        borderBottom: composerChannel === "internal_note" ? "2px solid var(--gold)" : "none",
                      }}
                    >
                      🔒 Staff Note
                    </button>
                  </div>
                )}

                {composerChannel === "email" && (
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Subject line..."
                    style={styles.subjectInput}
                  />
                )}

                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder={
                    composerChannel === "internal_note"
                      ? "Write an internal staff note (visible only to team)..."
                      : "Type your reply to guest..."
                  }
                  style={styles.composerTextarea}
                />

                <div style={styles.composerActions}>
                  <button
                    type="button"
                    onClick={handleAIDraft}
                    disabled={draftingReply}
                    style={styles.aidenBtn}
                    title="Generate smart AI draft based on guest conversation"
                  >
                    {draftingReply ? "AI drafting..." : "✦ Draft with AI"}
                  </button>

                  <button
                    type="button"
                    onClick={handleSendReply}
                    disabled={sendingReply || !replyBody.trim()}
                    style={{
                      ...styles.sendBtn,
                      background: composerChannel === "internal_note" ? "#e67e22" : "var(--gold, #F9AF36)",
                    }}
                  >
                    {sendingReply
                      ? "Sending..."
                      : composerChannel === "internal_note"
                      ? "Save Staff Note"
                      : "Send Message"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ─────────────────────────────────────────────────────────────
          PANE 3: Guest Dossier & Outcomes (Desktop Right / Mobile Modal)
      ───────────────────────────────────────────────────────────── */}
      {(!isMobile || mobileDossierOpen) && selectedThread && (
        <aside
          style={{
            ...styles.dossierPane,
            ...(isMobile
              ? {
                  position: "fixed",
                  inset: 0,
                  width: "100%",
                  zIndex: 10040,
                }
              : {}),
          }}
        >
          <div style={styles.paneRightContainer}>
            {isMobile && (
              <div style={styles.mobileDossierHeader}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "var(--gold)" }}>
                  Guest Details & Triage
                </h3>
                <button
                  type="button"
                  onClick={() => setMobileDossierOpen(false)}
                  style={styles.closeDossierBtn}
                >
                  ✕ Close
                </button>
              </div>
            )}

            {/* Guest Dossier */}
            <div style={styles.profileSection}>
              <h3 style={styles.sectionTitle}>Guest Dossier</h3>
              {customer ? (
                <div style={styles.profileCard}>
                  <div style={styles.avatarRow}>
                    <div style={styles.avatar}>
                      {customer.first_name.slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <strong style={styles.profileName}>
                        {customer.display_name || `${customer.first_name} ${customer.last_name || ""}`}
                      </strong>
                      <div style={styles.profileMeta}>
                        Guest since {new Date(customer.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div style={styles.dossierInfoList}>
                    <div style={styles.infoField}>
                      <span style={styles.infoLabel}>Email</span>
                      <strong style={styles.infoVal}>{customer.email || "—"}</strong>
                    </div>

                    <div style={styles.infoField}>
                      <span style={styles.infoLabel}>Phone</span>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong style={styles.infoVal}>{customer.phone || "—"}</strong>
                        {customer.phone && (
                          <button
                            type="button"
                            onClick={() => handleInitiateCall(customer.phone!)}
                            disabled={callState !== "idle"}
                            style={styles.callGuestInlineBtn}
                          >
                            📞 CALL
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={styles.infoField}>
                      <span style={styles.infoLabel}>Marketing Consent</span>
                      <strong style={styles.infoVal}>
                        SMS: {customer.sms_consent ? "Yes" : "No"} · Email: {customer.email_consent ? "Yes" : "No"}
                      </strong>
                    </div>

                    {customer.notes && (
                      <div style={styles.notesBlock}>
                        <span style={styles.infoLabel}>Internal Customer Notes</span>
                        <p style={styles.notesText}>{customer.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={styles.anonymousCard}>
                  {isGoogleReviewThread(selectedThread) ? (
                    <>
                      <span style={{ fontSize: 28 }}>⭐</span>
                      <strong style={{ margin: "6px 0 2px", fontSize: 13, color: "var(--gold)" }}>
                        {getContactDisplayName(selectedThread)}
                      </strong>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--muted)" }}>
                        Public feedback from Google Business Profile. Reply directly using the composer below.
                      </p>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 24 }}>👤</span>
                      <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>
                        Contact not yet registered in CRM.
                      </p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--gold)" }}>
                        {selectedThread.identity_norm}
                      </p>
                    </>
                  )}

                  <div style={{ marginTop: 16 }}>
                    {!isLinkingContact ? (
                      <button
                        type="button"
                        onClick={() => setIsLinkingContact(true)}
                        style={{ ...styles.actionBtn, width: '100%' }}
                      >
                        🔗 Link CRM Contact
                      </button>
                    ) : (
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                          <input
                            type="text"
                            placeholder="Search name, phone, email..."
                            value={linkContactQuery}
                            onChange={(e) => setLinkContactQuery(e.target.value)}
                            style={{ ...styles.searchInput, flex: 1 }}
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setIsLinkingContact(false);
                              setLinkContactQuery("");
                              setLinkContactResults([]);
                            }}
                            style={{ ...styles.actionBtn, padding: "8px" }}
                          >
                            ✕
                          </button>
                        </div>
                        
                        {searchingLinkContact && <p style={{ fontSize: 11, color: 'var(--muted)' }}>Searching...</p>}
                        
                        {!searchingLinkContact && linkContactResults.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 150, overflowY: 'auto' }}>
                            {linkContactResults.map(res => (
                              <div
                                key={res.id}
                                onClick={() => handleLinkContact(res)}
                                style={{
                                  padding: "8px",
                                  background: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.1)",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  fontSize: 11
                                }}
                              >
                                <strong style={{ color: "var(--text)" }}>{res.first_name} {res.last_name || ""}</strong>
                                <div style={{ color: "var(--muted)", marginTop: 2 }}>{res.phone || res.email || "No contact info"}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {!searchingLinkContact && linkContactQuery.length > 2 && linkContactResults.length === 0 && (
                          <p style={{ fontSize: 11, color: 'var(--muted)' }}>No CRM contacts found.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Task Wrap-up & Triage Outcomes */}
            <div style={styles.outcomesSection}>
              <h3 style={styles.sectionTitle}>Triage & Outcomes</h3>

              <div style={styles.dispoSelectGroup}>
                <label style={styles.dispoLabelHeader}>Thread Status</label>
                <select
                  value={dispositionStatus}
                  onChange={(e) => setDispositionStatus(e.target.value as any)}
                  style={styles.dispoSelect}
                >
                  <option value="resolved">✓ Resolved / Close</option>
                  <option value="waiting_guest">⏳ Parked — Waiting on Guest</option>
                  <option value="waiting_staff">⏳ Parked — Waiting on Staff / Mgr</option>
                  <option value="escalated">🚨 Escalated to Management</option>
                  <option value="spam">🚫 Spam / Quarantined</option>
                </select>
              </div>

              <div style={styles.dispoInputGroup}>
                <label style={styles.dispoLabelHeader}>Resolution / Disposition Note</label>
                <input
                  type="text"
                  value={dispositionNote}
                  onChange={(e) => setDispositionNote(e.target.value)}
                  placeholder="e.g., Reservation confirmed for 4, answered hours..."
                  style={styles.dispoInput}
                />
              </div>

              {/* 1-Tap Quick Resolution Tags */}
              <div style={{ marginBottom: 14 }}>
                <span style={styles.quickTagHeader}>Quick Resolution Tags:</span>
                <div style={styles.quickTagGrid}>
                  {RESOLUTION_TAGS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleUpdateStatus("resolved", tag)}
                      style={styles.quickTagChip}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleUpdateStatus()}
                disabled={updatingStatus}
                style={styles.dispoBtn}
              >
                {updatingStatus ? "Updating..." : "Update Thread Status"}
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL 1: + New Message Outbound Composer Modal
      ───────────────────────────────────────────────────────────── */}
      {isNewMessageOpen && (
        <div style={styles.modalBackdrop} onClick={() => setIsNewMessageOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>💬 New Communication</h3>
                <p style={styles.modalSubtitle}>Send an outbound SMS or Email to a guest</p>
              </div>
              <button
                type="button"
                onClick={() => setIsNewMessageOpen(false)}
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>

            {/* Recipient Selection */}
            <div style={styles.modalBody}>
              <label style={styles.fieldLabel}>Recipient</label>

              {selectedNewCustomer ? (
                <div style={styles.selectedCustomerBanner}>
                  <div>
                    <strong>
                      {selectedNewCustomer.display_name ||
                        `${selectedNewCustomer.first_name} ${selectedNewCustomer.last_name || ""}`}
                    </strong>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>
                      {selectedNewCustomer.phone || selectedNewCustomer.email}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedNewCustomer(null)}
                    style={styles.removeCustomerBtn}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="text"
                    value={newRecipientQuery}
                    onChange={(e) => setNewRecipientQuery(e.target.value)}
                    placeholder="Search guest CRM by name, phone, or email..."
                    style={styles.modalInput}
                  />

                  {searchingCustomers && (
                    <p style={{ margin: "4px 0", fontSize: 11, color: "var(--muted)" }}>Searching CRM...</p>
                  )}

                  {customerSearchResults.length > 0 && (
                    <div style={styles.customerDropdown}>
                      {customerSearchResults.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => {
                            setSelectedNewCustomer(c);
                            if (c.phone) setNewChannel("sms");
                            else if (c.email) setNewChannel("email");
                          }}
                          style={styles.customerResultRow}
                        >
                          <strong>{c.display_name || `${c.first_name} ${c.last_name || ""}`}</strong>
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>
                            {c.phone || c.email}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {!selectedNewCustomer && (
                    <div style={styles.manualEntryBox}>
                      <span style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 6 }}>
                        Or enter direct recipient details:
                      </span>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginBottom: 8 }}>
                        <input
                          type="text"
                          placeholder="Guest Name (optional)"
                          value={newDirectName}
                          onChange={(e) => setNewDirectName(e.target.value)}
                          style={styles.modalInput}
                        />
                        <input
                          type="tel"
                          placeholder="Phone Number (SMS)"
                          value={newDirectPhone}
                          onChange={(e) => {
                            setNewDirectPhone(e.target.value);
                            setNewChannel("sms");
                          }}
                          style={styles.modalInput}
                        />
                      </div>
                      <input
                        type="email"
                        placeholder="Email Address (optional)"
                        value={newDirectEmail}
                        onChange={(e) => setNewDirectEmail(e.target.value)}
                        style={styles.modalInput}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Channel Selector */}
              <div style={{ margin: "14px 0 10px" }}>
                <label style={styles.fieldLabel}>Channel</label>
                <div style={{ display: "flex", gap: 12 }}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="newChannel"
                      checked={newChannel === "sms"}
                      onChange={() => setNewChannel("sms")}
                    />
                    📱 SMS Text Message
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="newChannel"
                      checked={newChannel === "email"}
                      onChange={() => setNewChannel("email")}
                    />
                    ✉️ Email
                  </label>
                </div>
              </div>

              {newChannel === "email" && (
                <div style={{ marginBottom: 10 }}>
                  <label style={styles.fieldLabel}>Subject Line</label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    placeholder="Subject..."
                    style={styles.modalInput}
                  />
                </div>
              )}

              {/* Template Chips for Quick Start */}
              <div style={{ marginBottom: 8 }}>
                <span style={styles.templateHeader}>Quick Templates:</span>
                <div style={styles.templateChipsRow}>
                  {QUICK_TEMPLATES.map((tmpl) => (
                    <button
                      key={tmpl.label}
                      type="button"
                      onClick={() => setNewBody((prev) => (prev ? `${prev} ${tmpl.text}` : tmpl.text))}
                      style={styles.templateChip}
                    >
                      {tmpl.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={styles.fieldLabel}>Message Body</label>
                <textarea
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  placeholder="Type message..."
                  style={styles.modalTextarea}
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                onClick={() => setIsNewMessageOpen(false)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendNewOutboundMessage}
                disabled={sendingNewMessage || !newBody.trim()}
                style={styles.sendNewBtn}
              >
                {sendingNewMessage ? "Sending..." : "Send Outbound Message"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL 2: 1-Tap Quick Resolve Modal
      ───────────────────────────────────────────────────────────── */}
      {quickResolveOpen && selectedThread && (
        <div style={styles.modalBackdrop} onClick={() => setQuickResolveOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>✓ Resolve Conversation</h3>
                <p style={styles.modalSubtitle}>{selectedThread.customer_name || selectedThread.identity_norm}</p>
              </div>
              <button
                type="button"
                onClick={() => setQuickResolveOpen(false)}
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text)" }}>
                Select a resolution tag to complete and archive this conversation:
              </p>
              <div style={styles.quickTagGrid}>
                {RESOLUTION_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleUpdateStatus("resolved", tag)}
                    style={styles.quickTagChipBig}
                  >
                    ✓ {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL 3: Delete Confirmation Dialog
      ───────────────────────────────────────────────────────────── */}
      {deleteTarget && (
        <div style={styles.modalBackdrop} onClick={() => setDeleteTarget(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ ...styles.modalTitle, color: "#db5b48" }}>
                🗑️ Confirm Deletion
              </h3>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
                {deleteTarget.type === "thread"
                  ? `Are you sure you want to permanently delete the entire conversation thread for "${deleteTarget.name}"? This action cannot be undone.`
                  : "Are you sure you want to delete this message from the history?"}
              </p>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (deleteTarget.type === "thread") {
                    executeDeleteThread(deleteTarget.id);
                  } else {
                    executeDeleteMessage(deleteTarget.id);
                  }
                }}
                disabled={deletingTarget}
                style={styles.confirmDeleteBtn}
              >
                {deletingTarget ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  appContainer: {
    display: "flex",
    height: "100vh",
    width: "100%",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    overflow: "hidden",
    position: "relative",
  },
  toastBanner: {
    position: "fixed",
    top: 20,
    right: 20,
    background: "var(--gold, #F9AF36)",
    color: "#000",
    fontWeight: 800,
    fontSize: 12,
    padding: "10px 18px",
    borderRadius: 8,
    boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
    zIndex: 10100,
    animation: "fadeIn 0.2s ease-in-out",
  },
  queuePane: {
    flexShrink: 0,
    borderRight: "1px solid var(--border, #231D19)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "var(--bg, #0C0907)",
    position: "relative",
  },
  headerBlock: {
    padding: "14px 14px 10px",
    borderBottom: "1px solid var(--border, #231D19)",
  },
  headerTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  paneTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    margin: 0,
    fontWeight: 800,
    letterSpacing: 0.3,
  },
  newMessageBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#000",
    border: "none",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
  },
  triageCounterRow: {
    display: "flex",
    gap: 6,
    marginBottom: 10,
    flexWrap: "wrap",
  },
  triageCounterPill: {
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 8px",
    borderRadius: 12,
    border: "1px solid",
    background: "rgba(255,255,255,0.03)",
  },
  tabsRow: {
    display: "flex",
    gap: 8,
    marginBottom: 10,
    overflowX: "auto",
    paddingBottom: 2,
  },
  tabBtn: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    fontSize: 11,
    padding: "4px 2px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  searchInput: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--border, #231D19)",
    background: "rgba(255,255,255,0.05)",
    color: "var(--text)",
    fontSize: 12,
    outline: "none",
  },
  errorBanner: {
    color: "#DB5B48",
    background: "rgba(219, 91, 72, 0.08)",
    border: "1px solid rgba(219, 91, 72, 0.25)",
    padding: "10px",
    margin: "10px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "700",
  },
  threadScrollList: {
    flex: 1,
    overflowY: "auto",
    padding: 10,
  },
  emptyQueueBox: {
    padding: "40px 20px",
    textAlign: "center",
  },
  threadCard: {
    border: "1px solid",
    borderRadius: 12,
    padding: "12px 14px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "0.15s ease",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  cardName: {
    fontSize: 13,
    fontWeight: 800,
    color: "var(--text)",
  },
  cardTime: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
    flexWrap: "wrap",
  },
  channelBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  urgentBadge: {
    fontSize: 9,
    fontWeight: 800,
    color: "#DB5B48",
    background: "rgba(219, 91, 72, 0.12)",
    padding: "1px 5px",
    borderRadius: 4,
  },
  slaBadgeNew: {
    fontSize: 9,
    fontWeight: 800,
    color: "#27ae60",
    background: "rgba(39, 174, 96, 0.15)",
    padding: "1px 6px",
    borderRadius: 4,
  },
  slaBadgeWarm: {
    fontSize: 9,
    fontWeight: 800,
    color: "#e67e22",
    background: "rgba(230, 126, 34, 0.15)",
    padding: "1px 6px",
    borderRadius: 4,
  },
  slaBadgeStale: {
    fontSize: 9,
    fontWeight: 800,
    color: "#db5b48",
    background: "rgba(219, 91, 72, 0.18)",
    padding: "1px 6px",
    borderRadius: 4,
  },
  unreadDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "var(--gold)",
  },
  cardSnippet: {
    margin: "0 0 8px",
    fontSize: 12,
    lineHeight: 1.35,
    color: "var(--text-soft, #DCD9D4)",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 10,
    color: "var(--muted)",
  },
  scoreText: {
    fontWeight: 600,
  },
  dispoLabel: {
    color: "#73B676",
    fontWeight: 700,
  },
  mobileFab: {
    position: "absolute",
    bottom: 20,
    right: 20,
    background: "var(--gold, #F9AF36)",
    color: "#000",
    border: "none",
    borderRadius: 24,
    padding: "12px 20px",
    fontSize: 13,
    fontWeight: 800,
    boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
    cursor: "pointer",
    zIndex: 100,
  },
  messagePane: {
    flex: 1,
    borderRight: "1px solid var(--border, #231D19)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--bg, #0C0907)",
  },
  emptyView: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    textAlign: "center",
  },
  emptyText: {
    color: "var(--muted)",
    fontSize: 13,
    maxWidth: 320,
    margin: "0 0 16px",
  },
  emptyNewBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#000",
    border: "none",
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  threadDetailContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  threadHeader: {
    padding: "10px 16px",
    borderBottom: "1px solid var(--border, #231D19)",
    background: "var(--surface, #15110E)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  threadHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flex: "1 1 auto",
  },
  headerAvatarCircle: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255, 242, 218, 0.06)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    flexShrink: 0,
  },
  headerTitleGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  threadHeaderTopRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  threadHeaderSubRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: "var(--muted)",
    flexWrap: "wrap",
  },
  headerChannelPill: {
    fontSize: 10,
    fontWeight: 800,
    color: "#F0E6D4",
    background: "rgba(255, 255, 255, 0.07)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    padding: "2px 7px",
    borderRadius: 12,
    whiteSpace: "nowrap",
  },
  headerStatusPill: {
    fontSize: 9,
    fontWeight: 900,
    letterSpacing: 0.5,
    padding: "2px 7px",
    borderRadius: 12,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  },
  contactSubText: {
    color: "var(--text-soft, #DDD)",
    fontWeight: 600,
    fontSize: 11,
  },
  contactStaffMeta: {
    color: "var(--muted)",
    fontSize: 11,
  },
  backBtn: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid var(--border)",
    color: "var(--gold)",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  kicker: {
    color: "var(--gold)",
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  spamFlagBadge: {
    fontSize: 9,
    fontWeight: 800,
    color: "#fff",
    background: "#db5b48",
    padding: "2px 6px",
    borderRadius: 4,
    whiteSpace: "nowrap",
  },
  threadTitle: {
    margin: 0,
    fontSize: 16,
    fontFamily: "Georgia, serif",
    fontWeight: 700,
    color: "#FFF2DA",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  threadSub: {
    margin: 0,
    color: "var(--muted)",
    fontSize: 11,
  },
  threadHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  headerCallBtn: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#27ae60",
    borderRadius: 8,
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  headerResolveBtn: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    color: "var(--gold, #F9AF36)",
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  headerReopenBtn: {
    background: "rgba(255, 255, 255, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    color: "var(--text)",
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  headerInfoBtn: {
    background: "rgba(255, 255, 255, 0.08)",
    border: "1px solid var(--border)",
    color: "var(--text)",
    borderRadius: 8,
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerUtilityGroup: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  spamBtn: {
    background: "rgba(219, 91, 72, 0.08)",
    border: "1px solid rgba(219, 91, 72, 0.3)",
    color: "#db5b48",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  unmarkSpamBtn: {
    background: "rgba(39, 174, 96, 0.12)",
    border: "1px solid rgba(39, 174, 96, 0.3)",
    color: "#27ae60",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  deleteHeaderBtn: {
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid var(--border)",
    color: "var(--muted)",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 12,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  activeCallBanner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "rgba(115, 182, 118, 0.08)",
    borderBottom: "1px solid rgba(115, 182, 118, 0.25)",
    padding: "10px 18px",
  },
  callMuteBtn: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--border)",
    color: "var(--text)",
    padding: "6px 12px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  callHangUpBtn: {
    background: "#DB5B48",
    color: "#0C0907",
    border: "none",
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
  },
  messageTimeline: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  timelineItem: {
    display: "flex",
    width: "100%",
  },
  msgBubble: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid",
  },
  bubbleMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 4,
    fontSize: 9,
    color: "var(--muted)",
  },
  bubbleChannel: {
    fontWeight: 800,
    letterSpacing: 0.5,
  },
  bubbleTime: {
    fontStyle: "italic",
  },
  deleteMsgBtn: {
    background: "rgba(255, 255, 255, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: 4,
    color: "var(--muted)",
    fontSize: 10,
    cursor: "pointer",
    padding: "2px 5px",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  msgSubjectRow: {
    fontSize: 11,
    color: "var(--gold)",
    marginBottom: 4,
  },
  starsRow: {
    color: "var(--gold)",
    fontSize: 12,
    marginBottom: 4,
  },
  bubbleText: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
  },
  authorRow: {
    marginTop: 4,
    fontSize: 10,
    fontStyle: "italic",
    textAlign: "right",
    color: "var(--muted)",
  },
  templateChipsRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    background: "rgba(0,0,0,0.3)",
    borderTop: "1px solid var(--border, #231D19)",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  templateHeader: {
    fontSize: 11,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    textTransform: "uppercase",
    marginRight: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  templateChip: {
    background: "rgba(255, 242, 218, 0.08)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    color: "#FFF2DA",
    padding: "8px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    minHeight: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
    flexShrink: 0,
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  },
  composerCard: {
    padding: 12,
    borderTop: "1px solid var(--border, #231D19)",
    background: "var(--surface, #15110E)",
  },
  reviewAlertBanner: {
    background: "rgba(219, 91, 72, 0.08)",
    border: "1px solid rgba(219, 91, 72, 0.25)",
    color: "#DB5B48",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 10,
    fontWeight: 800,
    marginBottom: 8,
  },
  channelSelectRow: {
    display: "flex",
    gap: 12,
    fontSize: 11,
    marginBottom: 8,
  },
  composerLabel: {
    color: "var(--muted)",
    fontWeight: 700,
  },
  channelSelector: {
    background: "transparent",
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
    padding: "0 2px 2px",
  },
  subjectInput: {
    width: "100%",
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid var(--border, #231D19)",
    background: "rgba(255,255,255,0.03)",
    color: "var(--text)",
    fontSize: 12,
    marginBottom: 8,
    outline: "none",
  },
  composerTextarea: {
    width: "100%",
    minHeight: "65px",
    padding: 10,
    borderRadius: 8,
    border: "1px solid var(--border, #231D19)",
    background: "rgba(255,255,255,0.03)",
    color: "var(--text)",
    fontSize: 13,
    lineHeight: 1.4,
    outline: "none",
    resize: "vertical",
  },
  composerActions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 8,
  },
  aidenBtn: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    color: "var(--gold, #F9AF36)",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  sendBtn: {
    color: "#0C0907",
    border: "none",
    padding: "6px 16px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  dossierPane: {
    width: "320px",
    flexShrink: 0,
    height: "100%",
    background: "var(--surface, #15110E)",
  },
  paneRightContainer: {
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    height: "100%",
    overflowY: "auto",
  },
  mobileDossierHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 10,
    borderBottom: "1px solid var(--border)",
  },
  closeDossierBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
    margin: "0 0 10px",
  },
  profileSection: {},
  profileCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 12,
  },
  avatarRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    color: "var(--gold)",
  },
  profileName: {
    fontSize: 13,
    fontWeight: 800,
  },
  profileMeta: {
    fontSize: 10,
    color: "var(--muted)",
  },
  dossierInfoList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoField: {
    display: "flex",
    flexDirection: "column",
  },
  infoLabel: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--muted)",
    marginBottom: 1,
  },
  infoVal: {
    fontSize: 12,
    fontWeight: 700,
  },
  callGuestInlineBtn: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.3)",
    color: "#27ae60",
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 800,
    cursor: "pointer",
  },
  notesBlock: {
    marginTop: 4,
    borderTop: "1px solid var(--border)",
    paddingTop: 6,
  },
  notesText: {
    margin: 0,
    fontSize: 11,
    lineHeight: 1.35,
    color: "var(--text-soft)",
  },
  anonymousCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 16,
    textAlign: "center",
  },
  outcomesSection: {},
  dispoSelectGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 10,
  },
  dispoLabelHeader: {
    fontSize: 10,
    fontWeight: 800,
    color: "var(--muted)",
    marginBottom: 4,
  },
  dispoSelect: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid var(--border, #231D19)",
    background: "rgba(0,0,0,0.3)",
    color: "var(--text)",
    fontSize: 12,
    outline: "none",
  },
  dispoInputGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 12,
  },
  dispoInput: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid var(--border, #231D19)",
    background: "rgba(0,0,0,0.3)",
    color: "var(--text)",
    fontSize: 12,
    outline: "none",
  },
  quickTagHeader: {
    fontSize: 10,
    fontWeight: 800,
    color: "var(--muted)",
    display: "block",
    marginBottom: 6,
  },
  quickTagGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  quickTagChip: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#FFF2DA",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "0.15s ease",
  },
  quickTagChipBig: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--border)",
    color: "var(--text)",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
    flex: "1 1 calc(50% - 6px)",
  },
  dispoBtn: {
    width: "100%",
    padding: "10px",
    borderRadius: 8,
    border: "1px solid rgba(249, 175, 54, 0.3)",
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold)",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  mutedText: {
    padding: "20px 12px",
    textAlign: "center",
    color: "var(--muted)",
    fontSize: 12,
    fontStyle: "italic",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 10050,
  },
  modalContent: {
    background: "var(--surface, #18181b)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 14,
    width: "100%",
    maxWidth: 520,
    boxShadow: "0 10px 40px rgba(0,0,0,0.8)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 18px",
    borderBottom: "1px solid var(--border)",
  },
  modalTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
  },
  modalSubtitle: {
    margin: "2px 0 0",
    fontSize: 11,
    color: "var(--muted)",
  },
  modalCloseBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted)",
    fontSize: 16,
    cursor: "pointer",
  },
  modalBody: {
    padding: 18,
    maxHeight: "75vh",
    overflowY: "auto",
  },
  fieldLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 800,
    color: "var(--muted)",
    textTransform: "uppercase",
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  modalInput: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "rgba(0,0,0,0.3)",
    color: "var(--text)",
    fontSize: 12,
    outline: "none",
  },
  modalTextarea: {
    width: "100%",
    minHeight: "90px",
    padding: 10,
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "rgba(0,0,0,0.3)",
    color: "var(--text)",
    fontSize: 12,
    lineHeight: 1.4,
    outline: "none",
    resize: "vertical",
  },
  selectedCustomerBanner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    borderRadius: 8,
    background: "rgba(249, 175, 54, 0.08)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    marginBottom: 10,
  },
  removeCustomerBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    color: "var(--gold)",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
    cursor: "pointer",
  },
  customerDropdown: {
    border: "1px solid var(--border)",
    borderRadius: 6,
    background: "#111",
    marginTop: 4,
    marginBottom: 8,
    maxHeight: 160,
    overflowY: "auto",
  },
  customerResultRow: {
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
  },
  manualEntryBox: {
    padding: "10px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.02)",
    border: "1px solid var(--border)",
    marginTop: 8,
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "12px 18px",
    borderTop: "1px solid var(--border)",
    background: "rgba(0,0,0,0.2)",
  },
  cancelBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    color: "var(--muted)",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  sendNewBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#000",
    border: "none",
    padding: "8px 18px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  confirmDeleteBtn: {
    background: "#db5b48",
    color: "#fff",
    border: "none",
    padding: "8px 18px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
};